/**
 * Shared rendering and worked-step derivation for linear-equation topics.
 *
 * All equation generators share the same canonical LinearExpr/LinearEquation
 * model, so rendering to LaTeX and deriving the step-by-step solution live
 * here rather than being reimplemented per topic. Steps are derived directly
 * from the math, so they can never disagree with the displayed problem.
 */

import { Rational } from "../engine/rational";
import { LinearExpr, LinearEquation } from "../engine/linear";
import type { SolutionStep } from "./types";

/** Render `coeff*v + constant` to LaTeX, omitting zero/unit parts cleanly. */
export function exprToLatex(e: LinearExpr, v: string): string {
  const terms: string[] = [];

  // Variable term.
  if (!e.coeff.isZero()) {
    terms.push(coeffTerm(e.coeff, v));
  }
  // Constant term.
  if (!e.constant.isZero()) {
    terms.push(signedTerm(e.constant, terms.length === 0));
  }
  if (terms.length === 0) return "0";
  return terms.join(" ");
}

/** Leading coefficient*variable, e.g. "3x", "-x", "\\frac{1}{2}x". */
function coeffTerm(c: Rational, v: string): string {
  if (c.equals(Rational.ONE)) return v;
  if (c.equals(Rational.of(-1n))) return `-${v}`;
  return `${c.toLatex()}${v}`;
}

/** A term with an explicit +/- separator when not leading. */
function signedTerm(r: Rational, leading: boolean): string {
  if (leading) return r.toLatex();
  if (r.isNegative()) return `- ${r.neg().toLatex()}`;
  return `+ ${r.toLatex()}`;
}

export function equationToLatex(eq: LinearEquation, v: string): string {
  return `${exprToLatex(eq.left, v)} = ${exprToLatex(eq.right, v)}`;
}

/** Plain-text form used for the presentation cross-check parser. */
export function equationToText(eq: LinearEquation, v: string): string {
  return `${exprToText(eq.left, v)} = ${exprToText(eq.right, v)}`;
}

function exprToText(e: LinearExpr, v: string): string {
  const parts: string[] = [];
  if (!e.coeff.isZero()) {
    if (e.coeff.equals(Rational.ONE)) parts.push(v);
    else if (e.coeff.equals(Rational.of(-1n))) parts.push(`-${v}`);
    else parts.push(`${e.coeff.toString()}${v}`);
  }
  if (!e.constant.isZero()) {
    if (parts.length === 0) parts.push(e.constant.toString());
    else if (e.constant.isNegative())
      parts.push(`- ${e.constant.neg().toString()}`);
    else parts.push(`+ ${e.constant.toString()}`);
  }
  return parts.length === 0 ? "0" : parts.join(" ");
}

/**
 * Derive worked-solution steps for a one-solution linear equation, starting
 * from the already-simplified (distributed, like-terms-combined) canonical
 * form. Callers that want to show the distribution step can prepend the
 * original displayed form; this covers the post-simplification algebra:
 *   move variable terms -> move constants -> divide.
 */
export function deriveSolutionSteps(
  eq: LinearEquation,
  v: string,
): SolutionStep[] {
  const steps: SolutionStep[] = [];
  const sol = eq.solve();
  if (sol.category !== "one" || !sol.value) {
    // Special categories get a single explanatory "step".
    if (sol.category === "none") {
      steps.push({
        description:
          "The variable terms cancel and the remaining statement is false, so there is no solution.",
        latex: equationToLatex(eq, v),
      });
    } else {
      steps.push({
        description:
          "Both sides are identical, so every value works: infinitely many solutions.",
        latex: equationToLatex(eq, v),
      });
    }
    return steps;
  }

  // Work with left = ax + b, right = cx + d.
  const a = eq.left.coeff;
  const b = eq.left.constant;
  const c = eq.right.coeff;
  const d = eq.right.constant;

  // Step 1: move variable terms to the left: (a - c)x + b = d
  const ac = a.sub(c);
  const leftAfterVar = new LinearExpr(ac, b);
  const rightAfterVar = LinearExpr.constantExpr(d);
  if (!c.isZero()) {
    steps.push({
      description:
        c.isNegative()
          ? `Add ${c.neg().toString()}${v} to both sides.`
          : `Subtract ${c.toString()}${v} from both sides.`,
      latex: `${exprToLatex(leftAfterVar, v)} = ${exprToLatex(rightAfterVar, v)}`,
    });
  }

  // Step 2: move constant to the right: (a - c)x = d - b
  const db = d.sub(b);
  const leftAfterConst = new LinearExpr(ac, Rational.ZERO);
  if (!b.isZero()) {
    steps.push({
      description: b.isNegative()
        ? `Add ${b.neg().toString()} to both sides.`
        : `Subtract ${b.toString()} from both sides.`,
      latex: `${exprToLatex(leftAfterConst, v)} = ${db.toLatex()}`,
    });
  }

  // Step 3: divide by the coefficient.
  steps.push({
    description: `Divide both sides by ${ac.toString()}.`,
    latex: `${v} = ${sol.value.toLatex()}`,
  });

  return steps;
}
